import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { tourRegistry } from '@/tours';

const TourContext = createContext(null);

const STORAGE_KEY = 'prodaff_tour_state';

function loadState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveState(state) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

function clearState() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {}
}

export const TourProvider = ({ children }) => {
  // Only the tour's *name* + progress persist across reloads — never the
  // step definitions themselves. Steps are always resolved live from the
  // current tourRegistry below, so editing a tour's copy/targets/highlight
  // in src/tours takes effect immediately, even mid-tour, instead of a
  // stale array from a previous session silently winning forever.
  const [tourName, setTourName] = useState(() => loadState()?.tourName ?? null);
  const [currentIndex, setCurrentIndex] = useState(
    () => loadState()?.currentIndex ?? 0
  );
  const [callbackUrl, setCallbackUrl] = useState(
    () => loadState()?.callbackUrl ?? null
  );

  const steps = tourName ? (tourRegistry[tourName] ?? []) : [];
  const isActive = Boolean(tourName) && steps.length > 0;

  // Persist whenever tour state changes
  useEffect(() => {
    if (isActive) {
      saveState({ tourName, currentIndex, callbackUrl });
    } else {
      clearState();
    }
  }, [isActive, tourName, currentIndex, callbackUrl]);

  // Third-party widgets (Freshdesk chat, etc.) load/inject their own DOM
  // outside React's tree, so unmounting a wrapper component can't reliably
  // hide them (their script may finish loading after the unmount). Flag the
  // body instead so global.scss can force them out of the way with CSS.
  useEffect(() => {
    document.body.classList.toggle('tour-active', isActive);
    return () => document.body.classList.remove('tour-active');
  }, [isActive]);

  const startTour = useCallback((name) => {
    setTourName(name);
    setCurrentIndex(0);
  }, []);

  const endTour = useCallback(() => {
    setTourName(null);
    setCurrentIndex(0);
    setCallbackUrl(null);
    clearState();
  }, []);

  const next = useCallback((stepsLength) => {
    setCurrentIndex((prev) => {
      if (prev >= stepsLength - 1) return prev;
      return prev + 1;
    });
  }, []);

  const prev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }, []);

  return (
    <TourContext.Provider
      value={{
        isActive,
        steps,
        currentIndex,
        callbackUrl,
        setCallbackUrl,
        startTour,
        endTour,
        next,
        prev,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within TourProvider');
  return ctx;
};
